import { memo, useCallback, useMemo } from 'react';
import { toast } from 'sonner';

import { useT } from '@/app/i18n/client';
import CommonCalendar from '@/components/common/Calendar';
import type { IAccommodationRoomStatus, ISelectedDates } from '@/entities/accommodation/types';
import {
  utilDateDaysInMonth,
  utilDateFirstDayOfMonth,
  utilDateIsPast,
  utilDateToday,
} from '@/lib/date';
import { cn } from '@/lib/utils';

interface CheckInOutSectionProps {
  currentMonth: Date;
  selectedDates: ISelectedDates;
  roomStatusList: IAccommodationRoomStatus[];
  checkInTime?: string;
  checkOutTime?: string;
  handleDateClick: (date: Date) => void;
  handleChangeMonth: (date: Date) => (e: React.MouseEvent<HTMLButtonElement>) => void;
}

function AccommodationCheckInOutDate({
  currentMonth,
  selectedDates,
  checkInTime,
  checkOutTime,
  handleDateClick,
  handleChangeMonth,
  roomStatusList,
}: CheckInOutSectionProps) {
  const { t, i18n } = useT('product');

  const getNormalizedTime = useCallback((date: Date): number => {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  }, []);

  const isDateInRange = useCallback(
    (date: Date): boolean => {
      if (!selectedDates.checkIn) return false;
      const targetTime = getNormalizedTime(date);
      const checkInTime = getNormalizedTime(selectedDates.checkIn);
      if (selectedDates.checkOut) {
        const checkOutTime = getNormalizedTime(selectedDates.checkOut);
        return targetTime >= checkInTime && targetTime <= checkOutTime;
      } else {
        return targetTime === checkInTime;
      }
    },
    [getNormalizedTime, selectedDates.checkIn, selectedDates.checkOut],
  );

  const isCheckInDate = useCallback(
    (date: Date): boolean => {
      if (!selectedDates.checkIn) return false;
      return getNormalizedTime(date) === getNormalizedTime(selectedDates.checkIn);
    },
    [getNormalizedTime, selectedDates.checkIn],
  );

  const isCheckOutDate = useCallback(
    (date: Date): boolean => {
      if (!selectedDates.checkOut) return false;
      return getNormalizedTime(date) === getNormalizedTime(selectedDates.checkOut);
    },
    [getNormalizedTime, selectedDates.checkOut],
  );

  // 날짜 문자열 포맷 함수
  const getDateStr = useCallback((date: Date) => {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }, []);
  // roomPriceList에서 해당 날짜의 isActive 확인
  const getRoomPriceByDate = useCallback(
    (date: Date) => {
      const dateStr = getDateStr(date);
      return roomStatusList.find(r => r.date === dateStr);
    },
    [getDateStr, roomStatusList],
  );

  // 체크인(포함) ~ 체크아웃(전날)까지 모든 숙박일이 활성인지 검사
  const isStayRangeActive = useCallback(
    (checkInDate: Date, checkOutDate: Date): boolean => {
      // 숙박일은 체크인일부터 체크아웃 전날까지
      const cursor = new Date(
        checkInDate.getFullYear(),
        checkInDate.getMonth(),
        checkInDate.getDate(),
      );
      while (cursor < checkOutDate) {
        const price = getRoomPriceByDate(cursor);
        if (!price?.isActive) return false;
        cursor.setDate(cursor.getDate() + 1);
      }
      return true;
    },
    [getRoomPriceByDate],
  );

  // 날짜 클릭 가능 여부: 체크인은 활성일만, 체크아웃은 숙박 구간이 전부 활성일 때만 허용
  const canSelectDate = useCallback(
    (date: Date): boolean => {
      const targetDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      const monthStart = new Date(targetDate.getFullYear(), targetDate.getMonth(), 1);
      const isPast = utilDateIsPast(monthStart, targetDate.getDate());
      if (isPast) return false;

      // 체크인이 아직 없거나, 체크인/아웃이 모두 선택된 상태라면 => 새 체크인 선택 단계
      if (!selectedDates.checkIn || selectedDates.checkOut) {
        const price = getRoomPriceByDate(targetDate);
        return !!price?.isActive;
      }

      // 체크인이 존재하고 체크아웃 대기 상태
      const checkInDate = new Date(
        selectedDates.checkIn.getFullYear(),
        selectedDates.checkIn.getMonth(),
        selectedDates.checkIn.getDate(),
      );
      if (!checkInDate) return false;

      // 체크인을 재선택하려는 경우 (같거나 이전 날짜 클릭)
      if (targetDate.getTime() <= checkInDate.getTime()) {
        const price = getRoomPriceByDate(targetDate);
        return !!price?.isActive;
      }

      // 체크아웃 선택: 숙박일(체크인~체크아웃-1)이 모두 활성이어야 함
      return isStayRangeActive(checkInDate, targetDate);
    },
    [selectedDates.checkIn, selectedDates.checkOut, getRoomPriceByDate, isStayRangeActive],
  );

  const renderCalendar = useCallback((): React.JSX.Element[] => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const daysInMonth = utilDateDaysInMonth(currentMonth);
    const firstDay = utilDateFirstDayOfMonth(currentMonth);
    const prevMonthDate = new Date(year, month - 1, 1);
    const prevMonthDays = utilDateDaysInMonth(prevMonthDate);
    const totalCells = Math.ceil((firstDay + daysInMonth) / 7) * 7;
    const nextMonthDayCount = totalCells - firstDay - daysInMonth;

    const calendarCells: { date: Date; isCurrentMonth: boolean }[] = [];

    for (let i = firstDay - 1; i >= 0; i--) {
      const day = prevMonthDays - i;
      calendarCells.push({ date: new Date(year, month - 1, day), isCurrentMonth: false });
    }

    for (let day = 1; day <= daysInMonth; day++) {
      calendarCells.push({ date: new Date(year, month, day), isCurrentMonth: true });
    }

    for (let day = 1; day <= nextMonthDayCount; day++) {
      calendarCells.push({ date: new Date(year, month + 1, day), isCurrentMonth: false });
    }

    return calendarCells.map(({ date, isCurrentMonth }) => {
      const isInRange = isDateInRange(date);
      const isCheckIn = isCheckInDate(date);
      const isCheckOut = isCheckOutDate(date);
      const selectable = canSelectDate(date);
      const isSunday = date.getDay() === 0;
      const hasFullRange = !!(selectedDates.checkIn && selectedDates.checkOut);

      const getButtonClassName = (): string => {
        if (isCheckIn || isCheckOut) return 'bg-[#0064FF] text-white';
        if (!selectable) return 'cursor-not-allowed bg-[#F0F1F3] text-[#B0B0B0]';
        if (isInRange) return 'bg-transparent text-[#2E2E2E]';
        if (!isCurrentMonth) return 'text-gray-500';
        return `${isSunday ? 'text-red-500' : 'text-[#2E2E2E]'} hover:bg-[#F8F9FA]`;
      };

      return (
        <div key={date.toISOString()} className='relative h-12 max-sm:h-8'>
          {hasFullRange && (
            <>
              {isInRange && !isCheckIn && !isCheckOut && (
                <div className='absolute inset-y-0 left-[-4px] right-[-4px] z-0 bg-blue-100 max-sm:left-[-4px] max-sm:right-[-4px] sm:left-[-8px] sm:right-[-8px]' />
              )}
              {isCheckIn && !isCheckOut && (
                <div className='absolute inset-y-0 left-1/2 right-[-4px] z-0 bg-blue-100 max-sm:right-[-4px] sm:right-[-8px]' />
              )}
              {isCheckOut && !isCheckIn && (
                <div className='absolute inset-y-0 left-[-4px] right-1/2 z-0 bg-blue-100 max-sm:left-[-4px] sm:left-[-8px]' />
              )}
            </>
          )}
          <button
            onClick={e => {
              e.stopPropagation();
              if (!selectable) {
                toast.info(t('messages.canNotBooked'));
                return;
              }
              const normalizedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
              handleDateClick(normalizedDate);
            }}
            // disabled={!selectable}
            className={cn(
              'group relative z-10 m-auto flex h-12 w-12 flex-1 items-center justify-center rounded-xl text-base font-medium max-sm:h-8 max-sm:w-8 max-sm:text-sm',
              getButtonClassName(),
            )}
          >
            <span>{date.getDate()}</span>
          </button>
        </div>
      );
    });
  }, [
    t,
    currentMonth,
    isDateInRange,
    isCheckInDate,
    isCheckOutDate,
    canSelectDate,
    handleDateClick,
    selectedDates.checkIn,
    selectedDates.checkOut,
  ]);

  // roomPriceList의 min/max 날짜 계산
  const minDate = useMemo((): Date => {
    return utilDateToday();
  }, []);

  const isPrevMonthAvailable = useMemo((): boolean => {
    const prevMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1);
    return prevMonth >= new Date(minDate.getFullYear(), minDate.getMonth());
  }, [currentMonth, minDate]);

  const isNextMonthAvailable = useMemo((): boolean => {
    const nextMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1);
    const today = utilDateToday();
    const limitMonth = new Date(today.getFullYear(), today.getMonth() + 12);
    const withinTwelveMonths =
      nextMonth <= new Date(limitMonth.getFullYear(), limitMonth.getMonth());
    return withinTwelveMonths;
  }, [currentMonth]);

  return (
    <CommonCalendar
      title={`${t('checkIn')} - ${t('checkOut')}`}
      checkInTime={checkInTime}
      checkOutTime={checkOutTime}
      buttonLabel={
        selectedDates.checkIn && selectedDates.checkOut
          ? `${selectedDates.checkIn.toLocaleDateString(i18n.language, {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })} - ${selectedDates.checkOut.toLocaleDateString(i18n.language, {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}`
          : selectedDates.checkIn
            ? t('selectCheckOutDate')
            : ''
      }
      handleChangeMonth={handleChangeMonth}
      currentMonth={currentMonth}
      renderCalendar={renderCalendar}
      isPrevMonthAvailable={isPrevMonthAvailable}
      isNextMonthAvailable={isNextMonthAvailable}
    />
  );
}

export default memo(AccommodationCheckInOutDate);
